/* ──────────────────────────────────────────────────────────────────
   aeonNewsTake — converts a news item into AEON's short trading view.
   Pure JS, no deps. Keyword-mapped, fast, deterministic.

   Input:  item = { title, category, source, impact, sentiment, url }
   Output: { line: string, action: "BUY"|"SELL"|"WATCH"|"REDUCE"|"HOLD", color: "#xxx" }
   ────────────────────────────────────────────────────────────────── */

const ACTION_COLOR = {
  BUY:    "#4ade80",
  SELL:   "#ff5d6c",
  REDUCE: "#f5b042",
  WATCH:  "#4ad6ff",
  HOLD:   "rgba(255,255,255,0.4)",
};

// Bullish/bearish triggers keyed by topic
const RULES = [
  // ── Fed / rates ───────────────────────────────────────────────
  { match: ["rate cut", "dovish", "fed pivot", "easing"],
    action: "BUY", line: "Rate cut talk → risk-on. AEON leans long across crypto + high-beta equities." },
  { match: ["rate hike", "hawkish", "tighten"],
    action: "REDUCE", line: "Hawkish Fed signal → reducing risk exposure, tightening stops on longs." },
  { match: ["fomc", "fed chair", "powell speech", "fed meeting"],
    action: "WATCH", line: "FOMC volatility ahead — AEON stands down new entries until tape clears." },
  { match: ["inflation", "cpi", "ppi"],
    action: "WATCH", line: "Inflation print drives rate path — AEON monitoring breakevens + DXY for confirmation." },

  // ── Geopolitical / war / sanctions ────────────────────────────
  { match: ["war", "missile strike", "invasion", "nuclear"],
    action: "REDUCE", line: "Geopolitical shock — risk-off. AEON reducing leverage, raising cash buffer." },
  { match: ["sanction", "trade war", "tariff"],
    action: "WATCH", line: "Trade friction → expect equity drawdown, watch DXY + safe havens (BTC, gold)." },
  { match: ["iran", "russia", "ukraine", "middle east", "opec"],
    action: "WATCH", line: "Geopolitical risk live — energy + USD bid. AEON cautious on risk assets." },

  // ── Crypto specific bullish ──────────────────────────────────
  { match: ["etf approval", "etf inflow", "bitcoin etf"],
    action: "BUY", line: "ETF flows confirm institutional bid — AEON adds to BTC/ETH momentum positions." },
  { match: ["spot etf", "blackrock", "fidelity etf"],
    action: "BUY", line: "Institutional product → sticky bid. AEON bias long majors." },
  { match: ["adoption", "partnership", "integration"],
    action: "WATCH", line: "Bullish narrative — AEON watches for volume confirmation before sizing." },
  { match: ["bitlicense", "regulatory clarity", "approved"],
    action: "BUY", line: "Regulatory clarity → unlocks capital. AEON neutral-to-long." },
  { match: ["halving", "supply shock"],
    action: "BUY", line: "Supply-side bullish — AEON respects longer-frame trend, holds momentum longs." },

  // ── Crypto specific bearish ──────────────────────────────────
  { match: ["hack", "exploit", "drain", "rug", "exit scam"],
    action: "SELL", line: "Protocol hack → contagion risk. AEON cuts exposure to related ecosystem tokens." },
  { match: ["ban", "crackdown", "lawsuit", "doj", "sec sues", "indicted"],
    action: "REDUCE", line: "Regulatory hit — AEON trims exposure, gates favor structurally cleaner names." },
  { match: ["bankrupt", "liquidation", "wipeout", "insolvent"],
    action: "REDUCE", line: "Solvency event → cascade risk. AEON reduces leverage on related pairs." },
  { match: ["delist", "suspended", "halt trading"],
    action: "SELL", line: "Listing event impacts liquidity — AEON exits affected pair if exposed." },

  // ── Equity / macro broad ─────────────────────────────────────
  { match: ["recession", "yield curve", "credit spread"],
    action: "REDUCE", line: "Macro deterioration signal — AEON dialing back beta exposure across the book." },
  { match: ["earnings beat", "earnings miss", "guidance"],
    action: "WATCH", line: "Earnings volatility — AEON watches for spillover into crypto via tech correlation." },
  { match: ["nasdaq", "s&p", "dow", "stock market"],
    action: "WATCH", line: "Equity beta event — crypto often follows with a lag. AEON tracks correlation." },

  // ── Commodities ──────────────────────────────────────────────
  { match: ["oil price", "crude oil", "opec"],
    action: "WATCH", line: "Energy shock feeds inflation expectations — bearish for risk duration." },
  { match: ["gold", "silver"],
    action: "WATCH", line: "Precious metals strength signals macro fear — AEON watches BTC's correlation flip." },
];

// Category fallback lines
const CATEGORY_FALLBACK = {
  FED:         { action: "WATCH",  line: "Central bank signal — AEON monitoring rate-sensitive engines." },
  MACRO:       { action: "WATCH",  line: "Macro headline — second-order impact on crypto via DXY + yields." },
  EQUITIES:    { action: "WATCH",  line: "Equity-side news — beta correlation to crypto could spill over." },
  COMMODITIES: { action: "WATCH",  line: "Commodity move — feeds inflation expectations, watch crypto reaction." },
  GEO:         { action: "REDUCE", line: "Geopolitical headline — risk-off bias unless de-escalation confirms." },
  MARKETS:     { action: "WATCH",  line: "Market structure event — AEON tracking for sentiment shift." },
  CRYPTO:      { action: "WATCH",  line: "Crypto-native news — AEON evaluates for engine-specific edge." },
};

/**
 * Build AEON's take for a news item.
 * Returns { line, action, color } or null if title is empty.
 */
export function aeonNewsTake(item) {
  if (!item || !item.title) return null;
  const t = String(item.title).toLowerCase();

  // 1) Try keyword rules
  for (const rule of RULES) {
    if (rule.match.some(kw => t.includes(kw))) {
      return { line: rule.line, action: rule.action, color: ACTION_COLOR[rule.action] };
    }
  }

  // 2) Use sentiment override for crypto news
  const sentiment = item.sentiment;
  const sentLabel = typeof sentiment === "object"
    ? String(sentiment?.label || "").toUpperCase()
    : String(sentiment || "").toUpperCase();
  if (sentLabel === "BULLISH") {
    return { line: "Bullish framing — AEON looks for confirmation in price/volume before sizing.",
             action: "WATCH", color: ACTION_COLOR.WATCH };
  }
  if (sentLabel === "BEARISH") {
    return { line: "Bearish framing — AEON tightens existing longs, considers short setups on confirmation.",
             action: "REDUCE", color: ACTION_COLOR.REDUCE };
  }

  // 3) Category fallback
  const cat = String(item.category || "CRYPTO").toUpperCase();
  const fb = CATEGORY_FALLBACK[cat] || CATEGORY_FALLBACK.CRYPTO;
  return { line: fb.line, action: fb.action, color: ACTION_COLOR[fb.action] };
}

export const ACTION_COLORS = ACTION_COLOR;
